import { useRequest } from 'ahooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import SvgPanel from './SvgPanel';
import CircuitTable from './CircuitTable';
import Empty from '../Empty';
import api from '@/api';
import { useTranslation } from '@/hooks/useTranslation';

import styles from './index.module.less';

type DetailProps = {
  topicCode: string; // 文章code
  topicVersion: string; // 文章版本
  bookmapCode?: string;
  bookmapVersion?: string;
  defaultPCode?: string;
  paramMaps?: Record<string, string>; // 参数定义列表
};

type CircuitDiagramDetailProps = DetailProps & {
  openDrawer?: (params: { topicCode?: string; topicVersion?: string; pcode?: string }) => void;
};

const Detail = (props: CircuitDiagramDetailProps) => {
  const $ = useTranslation();
  const {
    defaultPCode = '',
    topicCode,
    topicVersion,
    bookmapCode,
    bookmapVersion,
    openDrawer
  } = props;
  const [selectPCode, setSelectPCode] = useState<string>(defaultPCode);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  const { data, loading, error, run } = useRequest(
    async () => {
      const params = {
        topicCode,
        topicVersion
      };
      const res: any = await api.sip.bookmap.getCircuitInfo({
        ...params,
        bookmapCode,
        bookmapVersion
      });

      if (res.code !== 200) {
        message.error(res.msg);
        throw new Error(res.msg);
      }

      const {
        svgUrl = '',
        topicName = '',
        definitionList = [],
        definitionColumnList = [],
        pcodeList = []
      } = res.data ?? {};

      const validPCodeList = definitionList.map((item: any) => item.pcode);
      return {
        svgUrl,
        topicName,
        definitionList,
        definitionColumnList,
        validPCodeList: validPCodeList.concat(pcodeList)
      };
    },
    { manual: true }
  );

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        if (entry.contentRect.height && containerRef.current) {
          setHeight(containerRef.current.clientHeight);
        }
      });
    });
    if (containerRef.current) {
      run();
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [run]);

  const onselectPCode = useCallback(
    (pcode: string) => {
      setSelectPCode(pcode);
      openDrawer?.({ topicCode, pcode, topicVersion });
    },
    [openDrawer, topicCode, topicVersion]
  );

  if (loading || error || !data) {
    return (
      <div className={styles.circuitDetail} ref={containerRef}>
        <Empty description={!error ? $('加载中...') : $('暂无数据')} />
      </div>
    );
  }

  return (
    <div className={styles.circuitDetail} ref={containerRef}>
      <div className={styles.title}>{data.topicName}</div>
      <SvgPanel
        fill={data.definitionList.length === 0}
        height={height}
        svgUrl={data.svgUrl}
        selectPCode={selectPCode}
        setSelectPCode={onselectPCode}
        defaultPCode={defaultPCode}
        paramMaps={props.paramMaps}
        validPCodeList={data.validPCodeList}
      />
      {data.definitionList.length > 0 ? (
        <CircuitTable
          tableData={data.definitionList}
          selectPCode={selectPCode}
          setSelectPCode={setSelectPCode}
          definitionColumnList={data.definitionColumnList}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default Detail;
